import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Link } from '@inertiajs/react';
import type { TimelineTone } from './timelineTone';
import { getTimelineToneStyles } from './timelineTone';
import { SentimentPill, type Sentiment } from './SentimentPill';
import type { ComponentType } from 'react';

export type TimelineFeedItemProps = {
  tone: TimelineTone;
  timeLabel: string;
  label: string;
  summary: string;
  href?: string | null;
  sentiment?: Sentiment | null;
  privacyLabel?: 'Privata' | 'Pubblica' | null;
  timeBadge?: { icon?: ComponentType<{ className?: string }>; label: string };
  metaBadges?: Array<{ icon?: ComponentType<{ className?: string }>; label: string }>;
};

export function TimelineFeedItem({
  tone,
  timeLabel,
  label,
  summary,
  href,
  sentiment,
  privacyLabel,
  timeBadge,
  metaBadges,
}: TimelineFeedItemProps) {
  const toneStyles = getTimelineToneStyles(tone);
  const Icon = toneStyles.icon;
  const isClickable = Boolean(href);
  const Wrapper = isClickable ? Link : 'div';
  const clickableProps = isClickable ? { href: href as string } : {};
  const TimeBadgeIcon = timeBadge?.icon;
  const privacyBadgeClassName =
    privacyLabel === 'Privata'
      ? 'border-amber-400/70 bg-amber-200/60 text-amber-900 dark:border-amber-400/60 dark:bg-amber-500/10 dark:text-amber-200'
      : 'border-emerald-400/70 bg-emerald-200/60 text-emerald-900 dark:border-emerald-400/60 dark:bg-emerald-500/10 dark:text-emerald-200';

  return (
    <Wrapper
      {...clickableProps}
      className={cn(
        'group flex items-start gap-3 rounded-lg border px-3 py-2',
        toneStyles.rowClassName,
        isClickable && 'transition no-underline cursor-pointer',
        isClickable && toneStyles.rowHoverClassName,
      )}
    >
      <div className={cn('mt-0.5 rounded-full p-2', toneStyles.iconWrapperClassName)}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 space-y-1">
        <div className="flex items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {timeBadge ? (
              <Badge variant="outline" className="gap-1">
                {TimeBadgeIcon && <TimeBadgeIcon className="h-3.5 w-3.5" />}
                {timeBadge.label}
              </Badge>
            ) : (
              <p className="text-sm font-semibold dark:text-slate-100 text-slate-800">
                {timeLabel}
              </p>
            )}
            <p className="text-sm font-semibold dark:text-slate-100 text-slate-800">{label}</p>
            {metaBadges?.map((badge, index) => {
              const BadgeIcon = badge.icon;
              return (
                <Badge key={`${badge.label}-${index}`} variant="outline" className="gap-1">
                  {BadgeIcon && <BadgeIcon className="h-3.5 w-3.5" />}
                  {badge.label}
                </Badge>
              );
            })}
            {privacyLabel && (
              <Badge variant="outline" className={privacyBadgeClassName}>
                {privacyLabel}
              </Badge>
            )}
          </div>
          {sentiment && (
            <div className="shrink-0">
              <SentimentPill sentiment={sentiment} />
            </div>
          )}
        </div>
        <div className="flex items-end justify-between gap-3">
          <p className="min-w-0 flex-1 text-sm dark:text-slate-300 text-slate-500">
            {summary || '—'}
          </p>
          {isClickable && (
            <span className="shrink-0 text-xs text-primary/80 opacity-0 transition group-hover:opacity-100">
              Vai al dettaglio
            </span>
          )}
        </div>
      </div>
    </Wrapper>
  );
}
